/**
 * Issue #548 — pin a surface from the map, not from a coordinate lookup.
 *
 * #543 shipped the add-a-surface form with the pin as two numeric text
 * fields, and Martin named the consequence the day it landed: "it requires
 * a player to find the coordinates for it, which is near impossible". Since
 * an unpinned facility is deliberately never drawn (`lib/facilities.js` —
 * no invented positions), coordinates-only meant every surface added
 * through the app arrived invisible, and the field-level map degraded to a
 * list.
 *
 * This module is the RULES of the pin gesture — `lib/facilityAdd.js`'s
 * reason verbatim: this repo mounts no components in unit tests, so a rule
 * written inside JSX is a rule nothing can assert. The surfaces are
 * `components/SurfacePinMap.jsx` (the map machinery) and
 * `components/FacilityAddForm.jsx` (the form the pin reads back into).
 *
 * THE PIN VOCABULARY IS #379's, IMPORTED, NOT FORKED. `via` flags
 * (map/field), `isGroundPin`, `samePin`, `pinFromForm`, the 6-decimal
 * precision — all come from `lib/groundEdit.js`, because a surface pin and
 * a ground pin are the same kind of fact moved by the same two inputs, and
 * two copies of "which input moved it last" is how they drift.
 *
 * --- THE DISTANCE SANITY CHECK: WARN, NEVER BLOCK ---
 *
 * A surface pin a kilometre from its venue is overwhelmingly a mis-tap —
 * but not certainly one: the venue pin is explicitly approximate
 * (schema.ts: "the venue pin is approximate ... the surface pin is
 * authoritative"), so a surface can legitimately sit some way off it. The
 * ticket's call: warn at save ("that's 1.2 km from this ground — sure?"),
 * don't refuse. So `facilityPinWarning` produces prose and nothing here
 * feeds `facilityCreateBody`/`facilityDraftComplete` — a far pin keeps
 * Save enabled, which `facilityPin.test.js` asserts.
 *
 * THE THRESHOLD IS 1 KM, stated rather than derived: the largest single
 * cricket venue (ground + nets + outfields) spans a few hundred metres, so
 * 1 km is outside any one venue's honest extent while still leaving the
 * approximate venue pin plenty of slack. Below it, silence — a warning on
 * every pin 200 m from an approximate venue centre would train people to
 * ignore the one that matters.
 */

import { distanceToGroundKm, formatKm } from "./distance";
import { GROUND_PIN_PRECISION, isGroundPin, pinFromForm } from "./groundEdit";

/** Beyond this many km from the venue's own pin, the warning fires. */
export const FACILITY_PIN_WARN_KM = 1;

/** The hint shown ON the map while it is an input — #379's copy, reworded
 * for a surface. Said in one place so both map surfaces (the detail map
 * and the admin console's embedded picker) say the same thing. */
export const SURFACE_PIN_HINT = "Drag the pin, or tap the map, to place the surface";

/**
 * The draft with a pin written into its coordinate fields — the read-back.
 * `groundEdit.formWithPin`'s shape on `facilityAdd`'s field names
 * (latitude/longitude as strings, because inputs hold strings). Fixed to 6
 * decimals for `formWithPin`'s stated reason: a leaflet drag hands back 15
 * significant digits, and a field reading "-42.45012999999998" looks
 * broken, whatever it means.
 */
export function draftWithPin(draft, pin) {
  if (!isGroundPin(pin)) return draft;
  return {
    ...draft,
    latitude: pin.lat.toFixed(GROUND_PIN_PRECISION),
    longitude: pin.lng.toFixed(GROUND_PIN_PRECISION),
  };
}

/**
 * The distance warning, or `null` when there is nothing to say.
 *
 * Reads the DRAFT's fields (strings — the state the Save button reads),
 * not a pin object, so the warning can never disagree with what would
 * actually be sent: a half pair or an unparseable number yields no pin and
 * no warning, exactly as it yields no body. `null` too when the ground
 * itself is unpinned — with no venue pin there is no distance to warn
 * about, and the surface pin may be the first real position the venue has.
 *
 * The message names the distance (`formatKm` — the app's one way of saying
 * a distance out loud) and says the user may proceed: warn, never block.
 */
export function facilityPinWarning(draft, ground) {
  const pin = pinFromForm(draft);
  if (!pin) return null;
  const km = distanceToGroundKm(pin, ground);
  if (km === null || km <= FACILITY_PIN_WARN_KM) return null;
  return {
    km,
    message: `This pin is ${formatKm(km)} from the ground's own pin — a stray tap lands exactly like this. You can still save it if the surface really is there.`,
  };
}

/**
 * "Use my location" failure copy for THIS context. The mechanism is
 * `lib/nearMe.js`'s `readDevicePosition` — same permission handling, same
 * one-read-per-press, position never leaves the device until the user
 * chooses to save it as the pin — but its messages end "Type the ground
 * name instead", which is the ground PICKER's way out, not this form's.
 * Here the ways out are the map tap and the coordinate fields, so each
 * line points back at those. Keyed by the same reasons
 * `reasonForPositionError` produces, so the two stay one vocabulary.
 */
export const SURFACE_PIN_LOCATION_MESSAGES = Object.freeze({
  denied: "Location is turned off for this site. Tap the map, or type the coordinates, instead.",
  unavailable: "Your device couldn't work out where it is. Tap the map, or type the coordinates, instead.",
  timeout: "Finding your location took too long. Tap the map, or type the coordinates, instead.",
  unsupported: "This browser can't share your location. Tap the map, or type the coordinates, instead.",
  insecure: "Location needs a secure (https) connection. Tap the map, or type the coordinates, instead.",
});

/** One plain line for a failed location read — honest, and never a dead end. */
export function surfacePinFailureMessage(reason) {
  return SURFACE_PIN_LOCATION_MESSAGES[reason] || SURFACE_PIN_LOCATION_MESSAGES.unavailable;
}
